import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faBriefcase,
  faBuilding,
  faBusinessTime,
  faCalendar,
  faCoffinCross,
  faHousePersonLeave,
  faIndustry,
  faTasks,
  faUser,
  faUserSecret,
  faPhoneRotary,
  faMobileScreenButton,
  faEnvelope,
  faExclamationCircle,
  faHouseUser,
  faUserLock,
  faMoneyCheckDollarPen,
  faCartShopping,
  faMapMarkerAlt,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { PropsWithChildren } from 'react';
import { Link, Tooltip } from '@mui/material';
import { Box } from '../box';
import { CircularProgress } from '../circular-progress';
import { ColorPalette, TypographySettings } from '../config';
import { IPageTitleZoneProps, PageTitleZoneType } from './page-title-zone-props';
import { CopyToClipboard } from '../copy-to-clipboard/copy-to-clipboard';

export function getIcon(type: PageTitleZoneType): IconProp | undefined {
  switch (type) {
    case 'user':
      return faUser;
    case 'user-anonymous':
      return faUserSecret;
    case 'user-deceased':
      return faCoffinCross;
    case 'user-emigrated':
      return faHousePersonLeave;
    case 'company':
      return faIndustry;
    case 'list':
      return faTasks;
    case 'recruitment-requests':
      return faBriefcase;
    case 'create-recruitment-request':
      return faBusinessTime;
    case 'offer':
      return faBuilding;
    case 'activity':
      return faBuilding;
    case 'calendar':
      return faCalendar;
    case 'none':
    default:
      return undefined;
  }
}

export function PageTitleZone(props: PropsWithChildren<IPageTitleZoneProps>) {
  const {
    children,
    type,
    title,
    subtitle,
    address,
    telephone,
    mobilephone,
    email,
    jobcenter,
    isLoading,
    isSocialProviderContext,
    municipality,
    payingMunicipality,
    responsibleMunicipality,
  } = props;
  const icon = getIcon(type);

  function getAddressForGoogleMaps() {
    const elements = address?.split(' ');
    return elements?.join('+');
  }

  return (
    <Box
      data-vanguard="citizen-header"
      data-testid={PageTitleZone.testIds.root}
      sx={{
        backgroundColor: isSocialProviderContext ? ColorPalette.SocialProviderGreen : ColorPalette.ScrubsGreen,
        color: ColorPalette.DefaultTextColor,
        fontFamily: TypographySettings.DefaultFontFamily,
        p: 2,
      }}
    >
      <Box
        sx={{
          fontSize: 20,
          fontWeight: 600,
          mb: '5px',
        }}
      >
        {isLoading ? (
          <CircularProgress />
        ) : (
          icon && (
            <Box
              component="span"
              data-testid={PageTitleZone.testIds.icon}
              sx={{
                mr: '10px',
              }}
            >
              <FontAwesomeIcon icon={icon} />
            </Box>
          )
        )}
        <span data-testid={PageTitleZone.testIds.title}>{!isLoading && title}</span>
        <Box
          sx={{
            textAlign: 'right',
            float: 'right',
            clear: 'right',
          }}
        >
          <Box
            sx={{
              fontSize: 14,
              textAlign: 'right',
              float: 'right',
              clear: 'right',
            }}
          >
            <span data-testid={PageTitleZone.testIds.address}>
              {!isLoading && (
                <Box display="flex" gap={0.5} alignItems="center">
                  <Tooltip placement="right" title={<CopyToClipboard title="Kopiér adresse" toCopy={address || ''} toastText="Adresse kopieret" />}>
                    <span>{address}</span>
                  </Tooltip>
                  {address && (
                    <Tooltip title="Åbn i Google Maps">
                      <Box
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            color: 'primary.main',
                          },
                        }}
                        onClick={() => window.open(`https://www.google.dk/maps/place/${getAddressForGoogleMaps()}`)}
                      >
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              )}
            </span>
            {!isLoading && municipality && (
              <Box component="span" data-testid={PageTitleZone.testIds.municipality} sx={{ ml: '5px' }}>
                <Box component="span" sx={{ mr: '4px' }}>
                  <FontAwesomeIcon icon={faHouseUser} title="Bopælskommune" />
                </Box>
                <span title="Bopælskommune">{municipality}</span>
              </Box>
            )}
          </Box>
          {!isLoading && (responsibleMunicipality || payingMunicipality) && (
            <Box
              sx={{
                fontSize: 14,
                textAlign: 'right',
                float: 'right',
                clear: 'right',
              }}
            >
              {responsibleMunicipality && (
                <Box component="span" data-testid={PageTitleZone.testIds.responsibleMunicipality} sx={{ mr: payingMunicipality ? '10px' : 0 }}>
                  <Box component="span" sx={{ mr: '4px' }}>
                    <FontAwesomeIcon icon={faCartShopping} title="Handlekommune" />
                  </Box>
                  <span title="Handlekommune">{responsibleMunicipality}</span>
                </Box>
              )}
              {payingMunicipality && (
                <Box component="span" data-testid={PageTitleZone.testIds.payingMunicipality}>
                  <Box component="span" sx={{ mr: '4px' }}>
                    <FontAwesomeIcon icon={faMoneyCheckDollarPen} title="Betalingskommune" />
                  </Box>
                  <span title="Betalingskommune">{payingMunicipality}</span>
                </Box>
              )}
            </Box>
          )}
          <Box
            sx={{
              fontSize: 20,
              fontWeight: 600,
              textAlign: 'right',
              float: 'right',
              clear: 'right',
            }}
          >
            {!isLoading && telephone && (
              <Tooltip title={<CopyToClipboard title="Kopiér fastnet telefonnummer" toCopy={telephone || ''} toastText="Fastnet telefonnummer kopieret" />}>
                <Box component="span">
                  <Box component="span" sx={{ mr: '4px' }}>
                    <FontAwesomeIcon icon={faPhoneRotary} />
                  </Box>
                  <span data-testid={PageTitleZone.testIds.telephone}>{telephone}</span>
                </Box>
              </Tooltip>
            )}
            {!isLoading && mobilephone && (
              <Tooltip title={<CopyToClipboard title="Kopiér mobilnummer" toCopy={mobilephone || ''} toastText="Mobilnummer kopieret" />}>
                <Box component="span" sx={{ ml: '10px' }}>
                  <Box component="span" sx={{ mr: '4px' }}>
                    <FontAwesomeIcon icon={faMobileScreenButton} />
                  </Box>
                  <span data-testid={PageTitleZone.testIds.mobilephone}>{mobilephone}</span>
                </Box>
              </Tooltip>
            )}
            {!isLoading && email && (
              <Tooltip title={<CopyToClipboard title="Kopiér e-mailadresse" toCopy={email || ''} toastText="E-mailadresse kopieret" />}>
                <Link href={`mailto:${email}`} underline="none" color="inherit">
                  <Box component="span" sx={{ ml: '10px' }}>
                    <Box component="span" sx={{ mr: '4px' }}>
                      <FontAwesomeIcon icon={faEnvelope} />
                    </Box>
                    <span data-testid={PageTitleZone.testIds.email}>{email}</span>
                  </Box>
                </Link>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
      <Box
        data-testid={PageTitleZone.testIds.subtitle}
        sx={{
          fontSize: 16,
        }}
      >
        {!isLoading && subtitle}
      </Box>
      {!isLoading && jobcenter && (
        <Box
          data-testid={PageTitleZone.testIds.jobcenter}
          sx={{
            fontSize: 16,
            textAlign: 'right',
            color: ColorPalette.HotAurora,
          }}
        >
          <Box component="span" sx={{ mr: '4px' }}>
            <FontAwesomeIcon color={ColorPalette.HotAurora} icon={faUserLock} />
          </Box>
          <Tooltip title="Borgerens hovedjobcenter">
            <span>{!isLoading && jobcenter}</span>
          </Tooltip>
        </Box>
      )}
      {children}
    </Box>
  );
}

PageTitleZone.testIds = {
  root: 'ptz',
  title: 'ptz-title',
  icon: 'ptz-title-icon',
  subtitle: 'ptz-subtitle',
  address: 'ptz-address',
  telephone: 'ptz-telephone',
  mobilephone: 'ptz-mobilephone',
  jobcenter: 'ptz-jobcenter',
  email: 'ptz-email',
  noAssignments: 'ptz-no-assignments',
  municipality: 'ptz-municipality',
  payingMunicipality: 'ptz-paying-municipality',
  responsibleMunicipality: 'ptz-responsible-municipality',
};
